import { createFocusTrap } from "focus-trap";
import classNames from "classnames";
import Sb from "../abstract/StatefulBehavior";
import checkCanFocusTrap from "../../helpers/checkCanFocusTrap";
import { enhanceAnchorIntoButton } from "../../helpers/dom";
export default class OpenDialog extends Sb {
  constructor(el, props, refs) {
    super();

    this.state = {
      open: false,
    };

    this.el = el;
    this.props = props;
    this.refs = refs;

    if (this.shouldDisable) return this.removeDialogFromDOM();

    if (!this.dialogRef) return;

    if (this.requiresOpenRef && !this.openRef) return;

    this.initDialogClass = this.dialogRef.className;

    if (this.trapFocus) {
      this.trap = createFocusTrap(this.trapContainer, {
        escapeDeactivates: true,
        clickOutsideDeactivates: this.clickOutsideDeactivates,
        allowOutsideClick: this.allowOutsideClick,
        onDeactivate: () => this.setState({ open: false }),
        checkCanFocusTrap,
        initialFocus: this.initialFocusRef,
      });
    }

    this.update();
    this.bindToggles();
    this.bindBackdropClick();
  }

  get openRef() {
    return this.refs.open;
  }

  get closeRef() {
    return this.refs.close;
  }

  get dialogRef() {
    return this.refs.dialog;
  }

  get initialFocusRef() {
    const ref = this.props.initialFocusRef;
    return this.refs[ref];
  }

  get backdropRef() {
    // a backdrop exists when the dialog ref is not equal to the trap container
    // (i.e. the backdrop *is* this dialog ref)
    if (this.dialogRef === this.trapContainer) return null;
    return this.dialogRef;
  }

  get requiresOpenRef() {
    return this.props.requiresOpenRef ?? true;
  }

  get trapFocus() {
    if (typeof this.props.trapFocus !== "boolean") return true;
    return this.props.trapFocus;
  }

  get trapContainer() {
    if (this.refs.trapContainer) return this.refs.trapContainer;
    return this.closeRef ? this.dialogRef : this.el;
  }

  // Optionally disable toggle behavior on some routes
  // (e.g. to disable login dialog when on login page)
  get shouldDisable() {
    if (!this.props.disableOnPage) return false;
    return window.location.pathname === this.props.disableOnPage;
  }

  get clickOnBackdropDeactivates() {
    return this.props.clickOnBackdropDeactivates === true || false;
  }

  get allowOutsideClick() {
    return this.clickOnBackdropDeactivates;
  }

  clickOutsideDeactivates = (event) => {
    if (!this.props.clickOutsideDeactivates) return false;
    // deactivate only if click event is on backdrop
    return !!this.backdropRef && event.target === this.dialogRef;
  };

  update = () => {
    this.dialogRef.className = classNames(this.initDialogClass, {
      "is-open": this.state.open,
    });

    if (this.openRef) {
      this.openRef.setAttribute(
        "aria-expanded",
        this.state.open ? "true" : "false",
      );
    }

    if (this.trapFocus) {
      this.state.open ? this.trap.activate() : this.trap.deactivate();
      document.body.classList.toggle("has-scroll-lock", this.state.open);
    }

    if (this.state.open && !this.trapFocus && this.initialFocusRef) {
      setTimeout(() => this.initialFocusRef.focus(), 100);
    }
  };

  bindToggles() {
    if (this.openRef) {
      this.openRef.setAttribute("aria-controls", this.dialogRef.id);
      enhanceAnchorIntoButton(this.openRef, () =>
        this.setState({ open: this.closeRef ? true : !this.state.open }),
      );
    }

    if (this.closeRef) {
      this.closeRef.setAttribute("aria-controls", this.dialogRef.id);
      this.closeRef.addEventListener("click", () => {
        this.setState({ open: false });
        if (
          this.props.closeCallback &&
          typeof this.props.closeCallback === "function"
        ) {
          this.props.closeCallback();
        }
      });
    }
  }

  bindBackdropClick() {
    if (!this.clickOnBackdropDeactivates || !this.backdropRef) return;

    this.backdropRef.addEventListener("click", (event) => {
      if (event.target === this.backdropRef) this.setState({ open: false });
    });
  }

  removeDialogFromDOM() {
    if (!this.dialogRef) return;
    this.dialogRef.remove();
  }
}
